import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, matchRoutes, Navigate, RouterProvider } from 'react-router';
import { isApiError } from '../api/http';
import { AuthProvider } from '../auth/AuthProvider';
import { SIGN_IN_PATHS, SIGN_OUT_PATH } from '../auth/return-to';
import { applyCompanyColors } from '../features/companies/company-colors';
import { UploadProvider } from '../features/files/uploads';
import { I18nProvider, useI18n } from '../i18n';
import { Spinner, ToastProvider, TooltipLayer } from '../ui';
import { AuthCallback, RequireAuth, SignInAddress, SignOutAddress } from './auth-pages';
import { ErrorPage, NotFoundPage } from './ErrorPage';
import { Home } from './Home';
import { Shell } from './Shell';

const queryClient: QueryClient = new QueryClient({
  // A change refused because the company has just become read-only (TF-113): the company list is
  // asked again, so the banner that says why appears at once, not at the next refresh.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isApiError(error) && error.code === 'READ_ONLY') void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // A 403/404 will not change by asking again.
      retry: (failures, error) => !(isApiError(error) && error.status >= 400 && error.status < 500) && failures < 2,
    },
  },
});

/**
 * The pages come in one chunk per area (./areas): the first screen loads only the shell (sign-in,
 * navigation, languages, the UI kit), and an area's code arrives when one of its pages is first
 * opened. Moving between pages of the same area loads nothing more.
 */
function area<M>(load: () => Promise<M>) {
  return <K extends keyof M>(name: K) =>
    lazy(async () => ({ default: (await load())[name] as Extract<M[K], ComponentType<any>> }));
}

const loadDashboard = () => import('./areas/dashboard');
const files = area(() => import('./areas/files'));
const crm = area(() => import('./areas/crm'));
const calendar = area(() => import('./areas/calendar'));
const crmImport = area(() => import('./areas/crm-import'));
const dashboard = area(loadDashboard);
const settings = area(() => import('./areas/settings'));
const admin = area(() => import('./areas/admin'));
const anonymous = area(() => import('./areas/public'));

const FilesPage = files('FilesPage');
const FileSection = files('FileSection');
const EditorPage = files('EditorPage');
const TrashPage = files('TrashPage');
const SharedWithMePage = files('SharedWithMePage');
const SearchPage = files('SearchPage');
const SigningPage = files('SigningPage');
const DealsSection = crm('DealsSection');
const CrmListDetail = crm('CrmListDetail');
const DealPage = crm('DealPage');
const AccountsPage = crm('AccountsPage');
const CrmAccountPage = crm('CrmAccountPage');
const AccountGroupPage = crm('AccountGroupPage');
const ContactsPage = crm('ContactsPage');
const ContactPage = crm('ContactPage');
const TasksPage = crm('TasksPage');
const TaskPage = crm('TaskPage');
const ImportAccountsPage = crmImport('ImportAccountsPage');
const ImportContactsPage = crmImport('ImportContactsPage');
const ImportsPage = crmImport('ImportsPage');
const GoogleImportPage = crmImport('GoogleImportPage');
const MicrosoftImportPage = crmImport('MicrosoftImportPage');
const CalendarPage = calendar('CalendarPage');
const DashboardPage = dashboard('DashboardPage');
const AccountPage = settings('AccountPage');
const MembersPage = settings('MembersPage');
const TeamsPage = settings('TeamsPage');
const TeamPage = settings('TeamPage');
const CompanyPage = settings('CompanyPage');
const FieldsPage = settings('FieldsPage');
const TagsPage = settings('TagsPage');
const RelationTypesPage = settings('RelationTypesPage');
const DealLinesPage = settings('DealLinesPage');
const CommunicationPage = settings('CommunicationPage');
const MarketplacePage = settings('MarketplacePage');
const AssistantsPage = settings('AssistantsPage');
const SendingDomainPage = settings('SendingDomainPage');
const AutomationPage = settings('AutomationPage');
const RuleEditorPage = settings('RuleEditorPage');
const AuditPage = settings('AuditPage');
const PrivacyPage = settings('PrivacyPage');
const BillingPage = settings('BillingPage');
const FormsPage = settings('FormsPage');
const FormEditorPage = settings('FormEditorPage');
const AdminAuthCallback = admin('AdminAuthCallback');
const RequirePlatformAdmin = admin('RequirePlatformAdmin');
const AdminShell = admin('AdminShell');
const OverviewPage = admin('OverviewPage');
const PlansPage = admin('PlansPage');
const CompaniesPage = admin('CompaniesPage');
const CostsPage = admin('CostsPage');
const MarketingSpendPage = admin('MarketingSpendPage');
const MarketplaceCataloguePage = admin('MarketplaceCataloguePage');
const SuspiciousPage = admin('SuspiciousPage');
const ReportsPage = admin('ReportsPage');
const SanctionsPage = admin('SanctionsPage');
const PlatformSettingsPage = admin('SettingsPage');
const PlatformCompanyPage = admin('CompanyPage');
const PublicLinkPage = anonymous('PublicLinkPage');
const LegalPage = anonymous('LegalPage');
const ReportPage = anonymous('ReportPage');
const OpenSourcePage = anonymous('OpenSourcePage');
const SignupPage = anonymous('SignupPage');
const FormPage = anonymous('FormPage');
const MeetingAnswerPage = anonymous('MeetingAnswerPage');
const HandoffPage = anonymous('HandoffPage');
const GalleryPage = lazy(() => import('../design/GalleryPage'));

// Most visits start on a dashboard, reached from the start page once the companies are known:
// its chunk loads now, beside that request, instead of after it.
if (/^\/(c\/[^/]+\/?)?$/.test(location.pathname)) void loadDashboard().catch(() => undefined);

function Loading() {
  const { t } = useI18n();
  return <Spinner label={t('common.loading')} />;
}

/** A page whose chunk may still be on its way: a spinner in its place until then. */
const page = (element: ReactNode) => <Suspense fallback={<Loading />}>{element}</Suspense>;

/** Whether the app has a page at a path: matched by a route other than the not-found catch-all (TF-235). */
const isAppPage = (path: string) => {
  const matches = matchRoutes(router.routes, path);
  return matches !== null && matches[matches.length - 1]!.route.path !== '*';
};

const router = createBrowserRouter([
  { path: '/auth-callback', element: <AuthCallback isPage={isAppPage} /> },
  // The addresses people type to sign in and out (TF-235): never "This page does not exist".
  ...SIGN_IN_PATHS.map((path) => ({ path, element: <SignInAddress /> })),
  { path: SIGN_OUT_PATH, element: <SignOutAddress /> },
  { path: '/signup', element: page(<SignupPage />), errorElement: <ErrorPage /> },
  // The platform owner's console: its own sign-in, against a realm of its own, so a company account
  // cannot open it and this one cannot open a company (docs/adr/0029-platform-admin.md).
  { path: '/admin/auth-callback', element: page(<AdminAuthCallback />), errorElement: <ErrorPage /> },
  {
    element: page(
      <RequirePlatformAdmin>
        <AdminShell />
      </RequirePlatformAdmin>,
    ),
    errorElement: <ErrorPage />,
    children: [
      { path: '/admin', element: page(<OverviewPage />) },
      { path: '/admin/plans', element: page(<PlansPage />) },
      { path: '/admin/marketing', element: page(<MarketingSpendPage />) },
      { path: '/admin/costs', element: page(<CostsPage />) },
      { path: '/admin/marketplace', element: page(<MarketplaceCataloguePage />) },
      { path: '/admin/suspicious', element: page(<SuspiciousPage />) },
      { path: '/admin/reports', element: page(<ReportsPage />) },
      { path: '/admin/sanctions', element: page(<SanctionsPage />) },
      { path: '/admin/settings', element: page(<PlatformSettingsPage />) },
      { path: '/admin/companies', element: page(<CompaniesPage />) },
      { path: '/admin/companies/:tenantId', element: page(<PlatformCompanyPage />) },
    ],
  },
  // Anonymous: a shared link opened by anybody.
  { path: '/s/:token', element: page(<PublicLinkPage />), errorElement: <ErrorPage /> },
  // Anonymous: a company's website form on its own page (TF-243).
  { path: '/f/:publicId', element: page(<FormPage />), errorElement: <ErrorPage /> },
  // Anonymous: answering a meeting's invitation from its e-mail (TF-363).
  { path: '/meeting', element: page(<MeetingAnswerPage />), errorElement: <ErrorPage /> },
  // Anonymous: the phone app's "Edit in browser", which signs this browser in (TF-369).
  { path: '/handoff', element: page(<HandoffPage />), errorElement: <ErrorPage /> },
  // The design gallery (TF-84): every component in every state. Loaded only when opened; the web
  // server refuses /design on production (DESIGN_GALLERY), and nothing links to it.
  { path: '/design', element: page(<GalleryPage />), errorElement: <ErrorPage /> },
  // Anonymous too: what anybody may read before signing in (TF-13).
  { path: '/privacy', element: page(<LegalPage name="privacy" />), errorElement: <ErrorPage /> },
  { path: '/copyright', element: page(<LegalPage name="copyright" />), errorElement: <ErrorPage /> },
  // How to delete an account, for Google Play's listing (TF-282).
  { path: '/delete-account', element: page(<LegalPage name="deletion" />), errorElement: <ErrorPage /> },
  // The data processing agreement and the sub-processors (TF-255).
  { path: '/legal/dpa', element: page(<LegalPage name="dpa" />), errorElement: <ErrorPage /> },
  { path: '/legal/subprocessors', element: page(<LegalPage name="subprocessors" />), errorElement: <ErrorPage /> },
  // The terms of service (TF-256).
  { path: '/legal/terms', element: page(<LegalPage name="terms" />), errorElement: <ErrorPage /> },
  // Reporting illegal content on a public link (TF-256).
  { path: '/legal/report', element: page(<ReportPage />), errorElement: <ErrorPage /> },
  // The open-source software in DoCuLat and its licences (TF-256).
  { path: '/legal/open-source', element: page(<OpenSourcePage />), errorElement: <ErrorPage /> },
  {
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    errorElement: <ErrorPage />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/shared', element: page(<SharedWithMePage />) },
      { path: '/account', element: page(<AccountPage />) },
      { path: '/c/:tenantId', element: page(<DashboardPage />) },
      { path: '/c/:tenantId/files', element: page(<FilesPage />) },
      { path: '/c/:tenantId/files/:folderId', element: page(<FilesPage />) },
      // A file's details beside its folder's list on a wide window (TF-98).
      { path: '/c/:tenantId/file/:fileId', element: page(<FileSection />) },
      { path: '/c/:tenantId/edit/:fileId', element: page(<EditorPage />) },
      { path: '/c/:tenantId/trash', element: page(<TrashPage />) },
      { path: '/c/:tenantId/search', element: page(<SearchPage />) },
      { path: '/c/:tenantId/settings', element: <Navigate to="members" replace /> },
      { path: '/c/:tenantId/settings/members', element: page(<MembersPage />) },
      { path: '/c/:tenantId/settings/teams', element: page(<TeamsPage />) },
      { path: '/c/:tenantId/settings/teams/:teamId', element: page(<TeamPage />) },
      { path: '/c/:tenantId/settings/company', element: page(<CompanyPage />) },
      { path: '/c/:tenantId/settings/fields', element: page(<FieldsPage />) },
      { path: '/c/:tenantId/settings/tags', element: page(<TagsPage />) },
      { path: '/c/:tenantId/settings/relations', element: page(<RelationTypesPage />) },
      { path: '/c/:tenantId/settings/deal-lines', element: page(<DealLinesPage />) },
      { path: '/c/:tenantId/settings/communication', element: page(<CommunicationPage />) },
      { path: '/c/:tenantId/settings/integrations', element: page(<MarketplacePage />) },
      { path: '/c/:tenantId/settings/assistants', element: page(<AssistantsPage />) },
      // A company's own sending domain, set up from its Marketplace entry (TF-211).
      { path: '/c/:tenantId/settings/sending-domain', element: page(<SendingDomainPage />) },
      { path: '/c/:tenantId/settings/automation', element: page(<AutomationPage />) },
      { path: '/c/:tenantId/settings/automation/:ruleId', element: page(<RuleEditorPage />) },
      { path: '/c/:tenantId/settings/forms', element: page(<FormsPage />) },
      { path: '/c/:tenantId/settings/forms/:formId', element: page(<FormEditorPage />) },
      { path: '/c/:tenantId/settings/audit', element: page(<AuditPage />) },
      { path: '/c/:tenantId/settings/privacy', element: page(<PrivacyPage />) },
      { path: '/c/:tenantId/settings/billing', element: page(<BillingPage />) },
      { path: '/c/:tenantId/signing', element: page(<SigningPage />) },
      { path: '/c/:tenantId/crm', element: <Navigate to="deals" replace /> },
      // List and detail (TF-98): the list is the section's route, the record its child - beside the list
      // on a wide window, instead of it on a narrow one. The deals board stays full width.
      { path: '/c/:tenantId/crm/deals', element: page(<DealsSection />), children: [{ path: ':dealId', element: page(<DealPage />) }] },
      {
        path: '/c/:tenantId/crm/accounts',
        element: page(<CrmListDetail list={<AccountsPage />} label="crm.accounts" />),
        children: [{ path: ':accountId', element: page(<CrmAccountPage />) }],
      },
      // A group's tree is a page of its own, full width.
      { path: '/c/:tenantId/crm/accounts/:accountId/group', element: page(<AccountGroupPage />) },
      {
        path: '/c/:tenantId/crm/contacts',
        element: page(<CrmListDetail list={<ContactsPage />} label="crm.contacts" kind="contact" />),
        children: [{ path: ':contactId', element: page(<ContactPage />) }],
      },
      { path: '/c/:tenantId/crm/tasks', element: page(<TasksPage />) },
      // A task as a page of its own (TF-374); ?task=<id> on any page is still the side sheet.
      { path: '/c/:tenantId/crm/tasks/:taskId', element: page(<TaskPage />) },
      { path: '/c/:tenantId/crm/calendar', element: page(<CalendarPage />) },
      { path: '/c/:tenantId/crm/import/accounts', element: page(<ImportAccountsPage />) },
      { path: '/c/:tenantId/crm/import/contacts', element: page(<ImportContactsPage />) },
      { path: '/c/:tenantId/crm/import/google', element: page(<GoogleImportPage />) },
      { path: '/c/:tenantId/crm/import/microsoft', element: page(<MicrosoftImportPage />) },
      { path: '/c/:tenantId/crm/imports', element: page(<ImportsPage />) },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

// A company's colours belong to its own pages (TF-109): a page outside every company - the legal pages,
// a public link, the console - shows DoCuLat's, however it was reached. Within the shell, the shell
// keeps them current as the company changes; a website form's own page (/f/) sets its company's.
router.subscribe(({ location }) => {
  if (!/^\/(c|f)\//.test(location.pathname)) void applyCompanyColors(null, null);
});

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <UploadProvider>
              <RouterProvider router={router} />
            </UploadProvider>
            {/* Hints on hover, keyboard focus and long press, for the whole app (TF-96). */}
            <TooltipLayer />
          </ToastProvider>
        </QueryClientProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
